// Foreign Registration fees per state. The panel appends "+ $199 service fee".
export const FOREIGN_REG: Record<string, string> = {
  Alabama: "$150",
  Alaska: "$350",
  Arizona: "$150",
  Arkansas: "$270",
  California: "$70 (⚠️ + $800 annual tax)",
  Colorado: "$100",
  Connecticut: "$120",
  Delaware: "$200",
  Florida: "$125",
  Georgia: "$225",
  Hawaii: "$50",
  Idaho: "$100",
  Illinois: "$150",
  Indiana: "$125",
  Iowa: "$100",
  Kansas: "$165",
  Kentucky: "$90",
  Louisiana: "$150",
  Maine: "$250",
  Maryland: "$100",
  Massachusetts: "$500",
  Michigan: "$50",
  Minnesota: "$185",
  Mississippi: "$250",
  Missouri: "$105",
  Montana: "$70",
  Nebraska: "$120",
  Nevada: "$75 (⚠️ extra license fees apply)",
  "New Hampshire": "$100",
  "New Jersey": "$125",
  "New Mexico": "$100",
  "New York": "$250 (⚠️ publication requirement)",
  "North Carolina": "$250",
  "North Dakota": "$135",
  Ohio: "$99",
  Oklahoma: "$300",
  Oregon: "$275",
  Pennsylvania: "$250",
  "Rhode Island": "$150",
  "South Carolina": "$110",
  "South Dakota": "$750",
  Tennessee: "$300 minimum",
  Texas: "$750",
  Utah: "$54",
  Vermont: "$125",
  Virginia: "$100",
  Washington: "$200",
  "West Virginia": "$150",
  Wisconsin: "$100",
  Wyoming: "$150",
  "Washington DC": "$220",
};

// Annual Report frequency (only non-standard "Annual" entries are included).
export const ANNUAL_REPORT: Record<string, string> = {
  Alaska: "Biennial",
  Georgia: "Biennial",
  Indiana: "Biennial",
  Iowa: "Biennial",
  Massachusetts: "Annual",
  Missouri: "None",
  Nebraska: "Biennial",
  "New Mexico": "None",
  "New York": "Biennial",
  Ohio: "None",
  Pennsylvania: "Decennial",
  "South Carolina": "Only if taxed as corp",
  Texas: "Annual (franchise report)",
  "Washington DC": "Biennial",
};

// Additional state-specific hyperlinks shown beneath the SoS link.
// Add { label, url } entries per state as needed.
export const EXTRA_LINKS: Record<string, { label: string; url: string }[]> = {
  Delaware: [
    {
      label: "Franchise Tax",
      url: "https://icis.corp.delaware.gov/Ecorp/logintax.aspx?FilingType=FranchiseTax",
    },
  ],
};

// States whose SoS business-search portal requires a VPN to access.
// Add states here as needed.
export const VPN_REQUIRED: Record<string, boolean> = {
  Illinois: true,
  Indiana: true,
  "South Carolina": true,
};

// Secretary of State (or analogous) business search URLs for every state + DC.
export const SOS_URLS: Record<string, string> = {
  Alabama: "https://www.sos.alabama.gov/government-records/business-entity-records",
  Alaska: "https://www.commerce.alaska.gov/cbp/main/Search/Entities",
  Arizona: "https://ecorp.azcc.gov/EntitySearch/Index",
  Arkansas: "https://www.sos.arkansas.gov/corps/search_all.php",
  California: "https://bizfileonline.sos.ca.gov/search/business",
  Colorado: "https://www.coloradosos.gov/biz/BusinessEntityCriteriaExt.do",
  Connecticut: "https://business.ct.gov/knowledge-base/articles/sots/download-results?language=en_US",
  Delaware: "https://icis.corp.delaware.gov/Ecorp/EntitySearch/NameSearch.aspx",
  Florida: "https://search.sunbiz.org/Inquiry/CorporationSearch/ByName",
  Georgia: "https://ecorp.sos.ga.gov/BusinessSearch",
  Hawaii: "https://hbe.ehawaii.gov/documents/search.html",
  Idaho: "https://sosbiz.idaho.gov/search/business",
  Illinois: "https://apps.ilsos.gov/businessentitysearch/",
  Indiana: "https://bsd.sos.in.gov/publicbusinesssearch",
  Iowa: "https://sos.iowa.gov/search/business/search.aspx",
  Kansas: "https://www.kansas.gov/bess/flow/main",
  Kentucky: "https://www.sos.ky.gov/bus/business-filings/OnlineServices/Pages/default.aspx",
  Louisiana: "https://coraweb.sos.la.gov/CommercialSearch/CommercialSearch.aspx",
  Maine: "https://icrs.informe.org/nei-sos-icrs/ICRS",
  Maryland: "https://egov.maryland.gov/BusinessExpress/EntitySearch",
  Massachusetts: "https://corp.sec.state.ma.us/CorpWeb/CorpSearch/CorpSearch.aspx",
  Michigan: "https://mibusinessregistry.lara.state.mi.us/search/business",
  Minnesota: "https://mblsportal.sos.mn.gov/Business/Search",
  Mississippi: "https://corp.sos.ms.gov/corp/portal/c/page/corpBusinessIdSearch/portal.aspx",
  Missouri: "https://bsd.sos.mo.gov/BusinessEntity/BESearch.aspx?SearchType=0",
  Montana: "https://biz.sosmt.gov/search/business",
  Nebraska: "https://www.nebraska.gov/sos/corp/corpsearch.cgi",
  Nevada: "https://esos.nv.gov/EntitySearch/OnlineEntitySearch",
  "New Hampshire": "https://quickstart.sos.nh.gov/online/BusinessInquire",
  "New Jersey": "https://www.njportal.com/DOR/BusinessNameSearch/Search/BusinessName",
  "New Mexico": "https://enterprise.sos.nm.gov/search",
  "New York": "https://apps.dos.ny.gov/publicInquiry/",
  "North Carolina": "https://www.sosnc.gov/online_services/search/by_title/_Business_Registration",
  "North Dakota": "https://firststop.sos.nd.gov/search/business",
  Ohio: "https://businesssearch.ohiosos.gov/",
  Oklahoma: "https://www.sos.ok.gov/corp/corpInquiryFind.aspx",
  Oregon: "https://sos.oregon.gov/business/Pages/find.aspx",
  Pennsylvania: "https://file.dos.pa.gov/search/business",
  "Rhode Island": "https://business.sos.ri.gov/CorpWeb/CorpSearch/CorpSearch.aspx",
  "South Carolina": "https://businessfilings.sc.gov/BusinessFiling/Entity/Search",
  "South Dakota": "https://sosenterprise.sd.gov/BusinessServices/Business/FilingSearch.aspx",
  Tennessee: "https://www.secstates.com/TN_Tennessee_Secretary_of_State_Corporation_Search",
  Texas: "https://comptroller.texas.gov/taxes/franchise/account-status/search",
  Utah: "https://businessregistration.utah.gov/EntitySearch/OnlineEntitySearch",
  Vermont: "https://bizfilings.vermont.gov/online/BusinessInquire/",
  Virginia: "https://www.secstates.com/VA_Virginia_Secretary_of_State_Corporation_Search",
  Washington: "https://ccfs.sos.wa.gov/#/AdvancedSearch",
  "West Virginia": "https://apps.wv.gov/sos/businessentitysearch/",
  Wisconsin: "https://apps.dfi.wi.gov/apps/corpsearch/search.aspx",
  Wyoming: "https://wyobiz.wyo.gov/Business/FilingSearch.aspx",
  "Washington DC": "https://os.dc.gov/page/corporate-registration",
};